import { CircularProgress, Grid, InputAdornment, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SectionTitle } from "./SectionTitle";
import { ControlledTextField } from "../forms/ControlledTextField";
import { ControlledCountryPhoneField } from "../forms/ControlledCountryPhoneField";
import { ControlledSelect } from "../forms/ControlledSelect";
import { CheckoutFormTypeFields, useCheckoutForm } from "./hooks/useCheckoutForm";
import {
    DOC_DNI_ID,
    OrderDeliveryAddressFields,
    OrderPersonalInformationFields,
} from "../../types/purchaseOrder/Order";
import { CHECKOUT_MAXLEN } from "../../utils/checkoutFieldMaxLengths";
import { textFieldStyle } from "./CouponStyles";
import { HttpCache, AfipResponsableTipoVista } from "../../http/httpCache";
import { HttpAfip } from "../../http/httpAfip";
import { EntityWithIdAndDescription } from "../../types/baseEntities";
import type { CheckoutFormType } from "./hooks/useCheckoutForm";
import type { Path } from "react-hook-form";

const CUIT_LENGTH = 11;

export const PersonalInformationSection: React.FC = () => {
    const { methods } = useCheckoutForm();
    const baseName = CheckoutFormTypeFields.PersonalInformation;

    const docTipoName =
        `${baseName}.${OrderPersonalInformationFields.DocumentoTipo}` as Path<CheckoutFormType>;
    const countryCodeName =
        `${baseName}.${OrderPersonalInformationFields.AreaCode}` as Path<CheckoutFormType>;
    const phoneName =
        `${baseName}.${OrderPersonalInformationFields.PhoneNumber}` as Path<CheckoutFormType>;
    const razonSocialName =
        `${baseName}.${OrderPersonalInformationFields.RazonSocial}` as Path<CheckoutFormType>;
    const afipTipoName =
        `${baseName}.${OrderPersonalInformationFields.AfipResponsableTipo}` as Path<CheckoutFormType>;

    const [documentoTipos, setDocumentoTipos] = useState<EntityWithIdAndDescription[]>([]);
    const [afipResponsableTipos, setAfipResponsableTipos] = useState<AfipResponsableTipoVista[]>(
        [],
    );

    useEffect(() => {
        HttpCache.getIdentityDocumentTypes()
            .then((tipos) => {
                setDocumentoTipos(tipos);
                const current = methods.getValues(docTipoName);
                if (current == null && tipos.length > 0) {
                    methods.setValue(docTipoName, tipos[0].id as any, { shouldValidate: false });
                }
            })
            .catch(() => {});
        HttpCache.getAfipResponsableTipos()
            .then(setAfipResponsableTipos)
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const silentTrigger = useCallback(
        () => Promise.resolve(true) as ReturnType<typeof methods.trigger>,
        [],
    );

    const docTipo = methods.watch(docTipoName) as number | null | undefined;
    const esDNI = docTipo === DOC_DNI_ID;

    const afipResponsableTiposFiltrados = useMemo(
        () =>
            docTipo == null
                ? afipResponsableTipos
                : afipResponsableTipos.filter(
                      (t) => t.codDocumentoTipo == null || t.codDocumentoTipo === docTipo,
                  ),
        [afipResponsableTipos, docTipo],
    );

    const [isLoadingAfip, setIsLoadingAfip] = useState(false);
    const lastFetchedCuitRef = useRef<string | null>(null);
    const afipDebounceRef = useRef<number | null>(null);

    const setAfipField = useCallback((name: any, value: any) => {
        methods.setValue(name, value, {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const clearAfipFields = useCallback(() => {
        setAfipField(razonSocialName, null);
        setAfipField(afipTipoName, null);
        setAfipField(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.Street}`,
            null,
        );
        setAfipField(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.StreetNumber}`,
            null,
        );
        setAfipField(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.LocalityDesc}`,
            null,
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCuitChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (afipDebounceRef.current != null) window.clearTimeout(afipDebounceRef.current);

        const digits = e.target.value.replace(/\D/g, "");
        if (digits.length !== CUIT_LENGTH || lastFetchedCuitRef.current === digits) return;

        afipDebounceRef.current = window.setTimeout(() => {
            lastFetchedCuitRef.current = digits;
            setIsLoadingAfip(true);
            HttpAfip.obtenerConstanciaInscripcion(digits)
                .then((constancia) => {
                    if (!constancia) {
                        clearAfipFields();
                        return;
                    }

                    const razonSocial =
                        constancia.razonSocial?.trim() ||
                        [constancia.apellido, constancia.nombre].filter(Boolean).join(", ") ||
                        null;

                    setAfipField(razonSocialName, razonSocial);
                    setAfipField(afipTipoName, constancia.codResponsableAfipTipo ?? null);

                    const dom = constancia.domicilioFiscal;
                    setAfipField(
                        `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.Street}`,
                        dom.calle || dom.direccion || null,
                    );
                    setAfipField(
                        `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.StreetNumber}`,
                        dom.nro || null,
                    );
                    setAfipField(
                        `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.LocalityDesc}`,
                        dom.localidad || null,
                    );
                })
                .catch(clearAfipFields)
                .finally(() => setIsLoadingAfip(false));
        }, 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const firstNamePath = `${baseName}.${OrderPersonalInformationFields.FirstName}` as const;
    const lastNamePath = `${baseName}.${OrderPersonalInformationFields.LastName}` as const;

    const handleNombreChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const apellido = (methods.getValues(lastNamePath as any) as string) ?? "";
        const generated = [apellido, e.target.value].filter(Boolean).join(", ");
        methods.setValue(razonSocialName, (generated || null) as any, { shouldValidate: false });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApellidoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const nombre = (methods.getValues(firstNamePath as any) as string) ?? "";
        const generated = [e.target.value, nombre].filter(Boolean).join(", ");
        methods.setValue(razonSocialName, (generated || null) as any, { shouldValidate: false });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Stack mb={2}>
            <SectionTitle>Datos Personales</SectionTitle>

            <Grid container spacing={2}>
                {esDNI ? (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nro Documento"
                                name={`${baseName}.${OrderPersonalInformationFields.Document}`}
                                required
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchDni,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nombre"
                                name={`${baseName}.${OrderPersonalInformationFields.FirstName}`}
                                required
                                onChange={handleNombreChange}
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchNombre }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Apellido"
                                name={`${baseName}.${OrderPersonalInformationFields.LastName}`}
                                required
                                onChange={handleApellidoChange}
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchApellido }}
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="CUIT / CUIL"
                                name={`${baseName}.${OrderPersonalInformationFields.Cuit}`}
                                required={docTipo != null}
                                onChange={handleCuitChange}
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchCuit,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                                InputProps={{
                                    endAdornment: isLoadingAfip ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={16} />
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Grid>
                        {docTipo != null && (
                            <>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledTextField
                                        control={methods.control}
                                        label="Razón social"
                                        name={razonSocialName}
                                        required
                                        inputProps={{ maxLength: CHECKOUT_MAXLEN.vchRazonSocial }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledSelect
                                        control={methods.control}
                                        name={afipTipoName}
                                        label="Condición frente al IVA"
                                        options={afipResponsableTiposFiltrados}
                                        sx={textFieldStyle}
                                    />
                                </Grid>
                            </>
                        )}
                    </>
                )}

                <Grid size={12}>
                    <ControlledCountryPhoneField<CheckoutFormType>
                        control={methods.control}
                        getValues={methods.getValues}
                        setValue={methods.setValue}
                        trigger={silentTrigger}
                        countryCodeName={countryCodeName}
                        phoneName={phoneName}
                        required
                        phoneMaxLength={CHECKOUT_MAXLEN.vchTelefono}
                        defaultCountryIso2="AR"
                        phoneHelperText='Ingresá código de área y número. Ej AR celular: " 9 11 1234 5678".'
                    />
                </Grid>
            </Grid>
        </Stack>
    );
};
